import { useState, useRef, useEffect } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, Upload, Trash2, Image, Globe, CreditCard, Banknote, Save, CheckCircle2, ArrowLeft, Clock, Zap } from "lucide-react";
import AutoInvoiceSection from "@/components/org-settings/AutoInvoiceSection";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { COMMON_TIMEZONES } from "@/hooks/use-timezone";

const EMPTY_BILLING = {
  companyName: "", taxId: "", addressLine1: "", addressLine2: "",
  city: "", state: "", postalCode: "", country: "",
  billingEmail: "", billingPhone: "",
  bankName: "", bankAccountName: "", bankAccountNumber: "",
  bankRoutingNumber: "", bankSwiftCode: "",
  zelleName: "", zelleEmail: "", zellePhone: "",
  venmoHandle: "", paypalEmail: "",
  defaultTaxRate: "", defaultTaxLabel: "",
};

export default function AdminOrgSettings() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [, setLocation] = useLocation();

  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const [orgName, setOrgName] = useState<string>("");
  const [timezone, setTimezone] = useState<string>("UTC");
  const [businessMode, setBusinessMode] = useState<string>("property_management");
  const [defaultCheckInTime, setDefaultCheckInTime] = useState<string>("15:00");
  const [defaultCheckOutTime, setDefaultCheckOutTime] = useState<string>("11:00");
  const [weekStartDay, setWeekStartDay] = useState<number>(0);
  const [calendarStartHour, setCalendarStartHour] = useState<number>(7);
  const [calendarEndHour, setCalendarEndHour] = useState<number>(18);
  const [generalSaved, setGeneralSaved] = useState(false);

  const [billing, setBilling] = useState({ ...EMPTY_BILLING });
  const [billingSaved, setBillingSaved] = useState(false);

  const { data: orgSettings } = useQuery<any>({
    queryKey: ["/api/admin/org-settings"],
    staleTime: 0,
  });

  const { data: orgProfile } = useQuery<any>({
    queryKey: ["/api/admin/org-billing-profile"],
  });

  useEffect(() => {
    if (orgSettings) {
      setOrgName(orgSettings.name || "");
      setTimezone(orgSettings.timezone || "UTC");
      setBusinessMode(orgSettings.businessMode || "property_management");
      setDefaultCheckInTime(orgSettings.defaultCheckInTime || "15:00");
      setDefaultCheckOutTime(orgSettings.defaultCheckOutTime || "11:00");
      setWeekStartDay(orgSettings.weekStartDay ?? 0);
      setCalendarStartHour(orgSettings.calendarStartHour ?? 7);
      setCalendarEndHour(orgSettings.calendarEndHour ?? 18);
    }
  }, [orgSettings]);

  useEffect(() => {
    if (orgProfile) {
      setBilling({
        companyName: orgProfile.companyName || "",
        taxId: orgProfile.taxId || "",
        addressLine1: orgProfile.addressLine1 || "",
        addressLine2: orgProfile.addressLine2 || "",
        city: orgProfile.city || "",
        state: orgProfile.state || "",
        postalCode: orgProfile.postalCode || "",
        country: orgProfile.country || "",
        billingEmail: orgProfile.billingEmail || "",
        billingPhone: orgProfile.billingPhone || "",
        bankName: orgProfile.bankName || "",
        bankAccountName: orgProfile.bankAccountName || "",
        bankAccountNumber: orgProfile.bankAccountNumber || "",
        bankRoutingNumber: orgProfile.bankRoutingNumber || "",
        bankSwiftCode: orgProfile.bankSwiftCode || "",
        zelleName: orgProfile.zelleName || "",
        zelleEmail: orgProfile.zelleEmail || "",
        zellePhone: orgProfile.zellePhone || "",
        venmoHandle: orgProfile.venmoHandle || "",
        paypalEmail: orgProfile.paypalEmail || "",
        defaultTaxRate: orgProfile.defaultTaxRate || "",
        defaultTaxLabel: orgProfile.defaultTaxLabel || "",
      });
    }
  }, [orgProfile]);

  const removeLogo = useMutation({
    mutationFn: () => apiRequest("PATCH", "/api/admin/org-settings", { logoUrl: null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/org-settings"] });
      toast({ title: "Logo removed" });
    },
    onError: () => toast({ title: "Failed to remove logo", variant: "destructive" }),
  });

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/gif", "image/webp", "image/svg+xml"];
    if (!allowed.includes(file.type)) {
      toast({ title: "Invalid file type", description: "PNG, JPG, WebP, or SVG only.", variant: "destructive" });
      return;
    }
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/api/upload", { method: "POST", body: formData, credentials: "include" });
      if (!res.ok) throw new Error("Upload failed");
      const { url } = await res.json();
      await apiRequest("PATCH", "/api/admin/org-settings", { logoUrl: url });
      qc.invalidateQueries({ queryKey: ["/api/admin/org-settings"] });
      toast({ title: "Logo updated" });
    } catch {
      toast({ title: "Failed to upload logo", variant: "destructive" });
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  }

  const formatHourDisplay = (h: number): string => {
    if (h === 0) return "12 AM";
    if (h < 12) return `${h} AM`;
    if (h === 12) return "12 PM";
    return `${h - 12} PM`;
  };

  const saveGeneral = useMutation({
    mutationFn: () => apiRequest("PATCH", "/api/admin/org-settings", { name: orgName, timezone, businessMode, defaultCheckInTime, defaultCheckOutTime, weekStartDay, calendarStartHour, calendarEndHour }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/org-settings"] });
      setGeneralSaved(true);
      setTimeout(() => setGeneralSaved(false), 2500);
      toast({ title: "Settings saved" });
    },
    onError: () => toast({ title: "Failed to save settings", variant: "destructive" }),
  });

  const saveBilling = useMutation({
    mutationFn: () => apiRequest("PUT", "/api/admin/org-billing-profile", billing),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/org-billing-profile"] });
      setBillingSaved(true);
      setTimeout(() => setBillingSaved(false), 2500);
      toast({ title: "Business profile saved" });
    },
    onError: () => toast({ title: "Failed to save profile", variant: "destructive" }),
  });

  function setB(field: string, value: string) {
    setBilling((prev) => ({ ...prev, [field]: value }));
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div className="flex items-start gap-3">
        <Button
          variant="ghost"
          size="sm"
          className="mt-0.5 -ml-2 text-muted-foreground hover:text-foreground"
          onClick={() => setLocation("/admin")}
          data-testid="button-back"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Organization Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your organization's branding, general settings, and business profile.</p>
        </div>
      </div>

      {/* Branding */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Image className="w-4 h-4 text-primary" />
            <CardTitle className="text-base">Branding</CardTitle>
          </div>
          <CardDescription>Your logo appears on invoices, PDFs, and signed documents.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            {orgSettings?.logoUrl ? (
              <div className="border rounded-lg p-3 bg-muted/30 flex items-center gap-3">
                <img
                  src={orgSettings.logoUrl}
                  alt="Organization logo"
                  className="max-h-12 max-w-[160px] object-contain"
                  data-testid="img-org-logo"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive shrink-0"
                  onClick={() => removeLogo.mutate()}
                  disabled={removeLogo.isPending}
                  data-testid="button-remove-logo"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Remove
                </Button>
              </div>
            ) : (
              <div className="border-2 border-dashed rounded-lg p-6 text-center text-muted-foreground w-48">
                <Image className="w-8 h-8 mx-auto mb-1 opacity-40" />
                <p className="text-xs">No logo set</p>
              </div>
            )}
            <div>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                className="hidden"
                onChange={handleLogoUpload}
                data-testid="input-logo-file"
              />
              <Button
                variant="outline"
                onClick={() => logoInputRef.current?.click()}
                disabled={logoUploading}
                data-testid="button-upload-logo"
              >
                <Upload className="w-4 h-4 mr-2" />
                {logoUploading ? "Uploading…" : orgSettings?.logoUrl ? "Replace Logo" : "Upload Logo"}
              </Button>
              <p className="text-xs text-muted-foreground mt-1.5">PNG, JPG, WebP, or SVG · max 5 MB</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* General Settings */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-primary" />
            <CardTitle className="text-base">General</CardTitle>
          </div>
          <CardDescription>Workspace name, timezone, and business configuration.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="orgName">Organization Name</Label>
            <p className="text-xs text-muted-foreground">Displayed across the platform and in outbound emails.</p>
            <Input
              id="orgName"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="My Property Management Co."
              data-testid="input-org-name"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="timezone">Display Timezone</Label>
              <p className="text-xs text-muted-foreground">Dates and times shown across the platform.</p>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger id="timezone" data-testid="select-org-timezone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {COMMON_TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="businessMode">Business Mode</Label>
              <p className="text-xs text-muted-foreground">Drives the dashboard default view, labels, and which revenue slices appear in reports. Hybrid lets you toggle between bookings and services views.</p>
              <Select value={businessMode} onValueChange={setBusinessMode}>
                <SelectTrigger id="businessMode" data-testid="select-business-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="property_management">Property Management — bookings, reservations, checkouts</SelectItem>
                  <SelectItem value="service_business">Services Business — cleaning / maintenance / check-in fees</SelectItem>
                  <SelectItem value="hybrid">Hybrid — both bookings and services</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Separator />
          <div>
            <div className="flex items-center gap-1.5 mb-3">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <p className="text-sm font-medium">Default Check-in / Check-out Times</p>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Organization-wide defaults used when creating reservations. Can be overridden per property manager or per apartment.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="checkInTime">Check-in Time</Label>
                <Input
                  id="checkInTime"
                  type="time"
                  value={defaultCheckInTime}
                  onChange={(e) => setDefaultCheckInTime(e.target.value)}
                  data-testid="input-check-in-time"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="checkOutTime">Check-out Time</Label>
                <Input
                  id="checkOutTime"
                  type="time"
                  value={defaultCheckOutTime}
                  onChange={(e) => setDefaultCheckOutTime(e.target.value)}
                  data-testid="input-check-out-time"
                />
              </div>
            </div>
          </div>
          <Separator />
          <div>
            <div className="flex items-center gap-1.5 mb-3">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <p className="text-sm font-medium">Week Start Day</p>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Sets which day the weekly view starts on in Cleaning Jobs.</p>
            <div className="w-48">
              <Select value={String(weekStartDay)} onValueChange={(v) => setWeekStartDay(Number(v))}>
                <SelectTrigger data-testid="select-week-start-day">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Sunday</SelectItem>
                  <SelectItem value="1">Monday</SelectItem>
                  <SelectItem value="2">Tuesday</SelectItem>
                  <SelectItem value="3">Wednesday</SelectItem>
                  <SelectItem value="4">Thursday</SelectItem>
                  <SelectItem value="5">Friday</SelectItem>
                  <SelectItem value="6">Saturday</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Separator />
          <div>
            <div className="flex items-center gap-1.5 mb-3">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <p className="text-sm font-medium">Calendar Display Hours</p>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Sets the time range shown in the Day and Week calendar views for cleaning jobs.</p>
            <div className="flex items-center gap-3 flex-wrap">
              <div>
                <Label className="text-xs mb-1.5 block">Start Hour</Label>
                <Select value={String(calendarStartHour)} onValueChange={(v) => setCalendarStartHour(Number(v))} data-testid="select-calendar-start-hour">
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={i} value={String(i)}>{formatHourDisplay(i)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <span className="text-sm text-muted-foreground mt-5">to</span>
              <div>
                <Label className="text-xs mb-1.5 block">End Hour</Label>
                <Select value={String(calendarEndHour)} onValueChange={(v) => setCalendarEndHour(Number(v))} data-testid="select-calendar-end-hour">
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={i} value={String(i)}>{formatHourDisplay(i)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={() => {
                if (calendarEndHour <= calendarStartHour) {
                  toast({ title: "Invalid calendar hours", description: "End hour must be after start hour", variant: "destructive" });
                  return;
                }
                saveGeneral.mutate();
              }}
              disabled={saveGeneral.isPending}
              data-testid="button-save-general"
            >
              {generalSaved ? (
                <><CheckCircle2 className="w-4 h-4 mr-2" />Saved</>
              ) : saveGeneral.isPending ? (
                "Saving…"
              ) : (
                <><Save className="w-4 h-4 mr-2" />Save</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Payment status banner */}
      <div className="flex items-center justify-between rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
        <div className="flex items-center gap-2 text-sm">
          <Zap className="w-4 h-4 text-primary" />
          {orgSettings?.stripeConnectOnboardingStatus === "complete" ? (
            <span className="text-green-700 font-medium">Online payments active</span>
          ) : orgSettings?.stripeConnectOnboardingStatus === "pending" ? (
            <span className="text-yellow-700">Payment setup in progress</span>
          ) : orgSettings?.stripeConnectOnboardingStatus === "restricted" ? (
            <span className="text-red-700">Payment account needs attention</span>
          ) : (
            <span>Online payments not set up yet</span>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => setLocation("/admin/billing-setup")}>
          {orgSettings?.stripeConnectOnboardingStatus === "complete" ? "Manage" : "Set up payments"}
        </Button>
      </div>

      {/* Business / Billing Profile */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-primary" />
            <CardTitle className="text-base">Business Profile</CardTitle>
          </div>
          <CardDescription>This information appears on invoices and documents sent to property managers.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Company Name</Label>
              <Input value={billing.companyName} onChange={(e) => setB("companyName", e.target.value)} placeholder="Coastal Living Properties LLC" data-testid="input-company-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Tax ID / EIN</Label>
              <Input value={billing.taxId} onChange={(e) => setB("taxId", e.target.value)} placeholder="12-3456789" data-testid="input-tax-id" />
            </div>
            <div className="space-y-1.5">
              <Label>Billing Email</Label>
              <Input type="email" value={billing.billingEmail} onChange={(e) => setB("billingEmail", e.target.value)} placeholder="billing@yourcompany.com" data-testid="input-billing-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Billing Phone</Label>
              <Input type="tel" value={billing.billingPhone} onChange={(e) => setB("billingPhone", e.target.value)} placeholder="+1 (305) 555-0100" data-testid="input-billing-phone" />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Address Line 1</Label>
              <Input value={billing.addressLine1} onChange={(e) => setB("addressLine1", e.target.value)} placeholder="123 Main Street" data-testid="input-address-1" />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Address Line 2</Label>
              <Input value={billing.addressLine2} onChange={(e) => setB("addressLine2", e.target.value)} placeholder="Suite 400" data-testid="input-address-2" />
            </div>
            <div className="space-y-1.5">
              <Label>City</Label>
              <Input value={billing.city} onChange={(e) => setB("city", e.target.value)} placeholder="Miami" data-testid="input-city" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>State</Label>
                <Input value={billing.state} onChange={(e) => setB("state", e.target.value)} placeholder="FL" data-testid="input-state" />
              </div>
              <div className="space-y-1.5">
                <Label>ZIP</Label>
                <Input value={billing.postalCode} onChange={(e) => setB("postalCode", e.target.value)} placeholder="33101" data-testid="input-postal" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Country</Label>
              <Input value={billing.country} onChange={(e) => setB("country", e.target.value)} placeholder="US" data-testid="input-country" />
            </div>
            <div className="space-y-1.5">
              <Label>Default Tax Rate (%)</Label>
              <Input type="number" step="0.01" min="0" max="100" value={billing.defaultTaxRate} onChange={(e) => setB("defaultTaxRate", e.target.value)} placeholder="0.00" data-testid="input-default-tax-rate" />
              <p className="text-xs text-muted-foreground">Applied to marketplace bookings unless the apartment has its own override.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Default Tax Label</Label>
              <Input value={billing.defaultTaxLabel} onChange={(e) => setB("defaultTaxLabel", e.target.value)} placeholder="Sales Tax" data-testid="input-default-tax-label" />
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            <div className="flex items-center gap-1.5">
              <CreditCard className="w-4 h-4 text-muted-foreground" />
              <p className="text-sm font-medium">Payment Details</p>
            </div>
            <p className="text-xs text-muted-foreground -mt-1">Payment instructions shown on invoices so property managers know how to pay you.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Bank Name</Label>
                <Input value={billing.bankName} onChange={(e) => setB("bankName", e.target.value)} placeholder="Chase Bank" data-testid="input-bank-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Account Name</Label>
                <Input value={billing.bankAccountName} onChange={(e) => setB("bankAccountName", e.target.value)} placeholder="Coastal Living LLC" data-testid="input-account-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Account Number</Label>
                <Input value={billing.bankAccountNumber} onChange={(e) => setB("bankAccountNumber", e.target.value)} placeholder="••••••••1234" data-testid="input-account-number" />
              </div>
              <div className="space-y-1.5">
                <Label>Routing Number</Label>
                <Input value={billing.bankRoutingNumber} onChange={(e) => setB("bankRoutingNumber", e.target.value)} placeholder="021000021" data-testid="input-routing" />
              </div>
              <div className="space-y-1.5">
                <Label>SWIFT / BIC</Label>
                <Input value={billing.bankSwiftCode} onChange={(e) => setB("bankSwiftCode", e.target.value)} placeholder="CHASUS33" data-testid="input-swift" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 pt-1">
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1">
                  <Banknote className="w-3 h-3" /> Zelle Name
                </Label>
                <Input value={billing.zelleName} onChange={(e) => setB("zelleName", e.target.value)} placeholder="John Smith" data-testid="input-zelle-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Zelle Email / Phone</Label>
                <Input value={billing.zelleEmail} onChange={(e) => setB("zelleEmail", e.target.value)} placeholder="billing@yourcompany.com" data-testid="input-zelle-email" />
              </div>
              <div className="space-y-1.5">
                <Label>Venmo Handle</Label>
                <Input value={billing.venmoHandle} onChange={(e) => setB("venmoHandle", e.target.value)} placeholder="@coastalliving" data-testid="input-venmo" />
              </div>
              <div className="space-y-1.5">
                <Label>PayPal Email</Label>
                <Input value={billing.paypalEmail} onChange={(e) => setB("paypalEmail", e.target.value)} placeholder="payments@yourcompany.com" data-testid="input-paypal" />
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={() => saveBilling.mutate()}
              disabled={saveBilling.isPending}
              data-testid="button-save-billing"
            >
              {billingSaved ? (
                <><CheckCircle2 className="w-4 h-4 mr-2" />Saved</>
              ) : saveBilling.isPending ? (
                "Saving…"
              ) : (
                <><Save className="w-4 h-4 mr-2" />Save Profile</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <AutoInvoiceSection orgSettings={orgSettings} timezone={timezone} />
    </div>
  );
}
